import React, {useState, useRef} from "react";
import {Box, Button, Divider, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper} from "@mui/material";
import FaModalInfo from "../FactureeComponents/FaModalInfo";
import ShowCode from "../helpers/ShowCode.js";

const FactureeCompModalInfoExmpl = () => {

  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<{ show: () => void }>(null);

  const openModal = () => {
    setIsModalOpen(true);
    modalRef.current?.show(); // Triggering the show method of the modal
  };

  const exampleCode = `<>
  {/* Button to trigger modal */}
  <Button variant={"contained"} onClick={() => console.log('Info modal would open')}>
    Open Modal
  </Button>

  {/* Modal info component */}
  <FaModalInfo
    title="Example Info Modal"
    maxWidth="lg"
    isOpen={true}
    setIsOpen={(isOpen) => console.log('Modal open state:', isOpen)}
  >
    {/* Content of the modal */}
    <Typography>
      This is the content of the modal. Lorem ipsum dolor sit amet, consectetuer adipiscing elit.
      Aenean commodo ligula eget dolor. Aenean massa.
    </Typography>
  </FaModalInfo>
</>`;

  return (
      <Box sx={{padding: '8rem 8vw 2rem 8vw'}}>
        <Typography variant={"h3"} sx={{marginBottom: '1rem'}}>FaModalInfo Component</Typography>
        
        <Typography variant={"h4"} sx={{marginBottom: '1.5rem', marginTop: '2.5rem'}}>Basic Info Modal Example</Typography>
        
        <Button variant={"contained"} onClick={openModal}>Open Modal</Button>
        
        <FaModalInfo
            ref={modalRef}
            title="Example Info Modal"
            maxWidth="lg"
            isOpen={isModalOpen}
            setIsOpen={setIsModalOpen}
        >
          {/* Content of the modal */}
          <Typography>This is the content of the modal. Lorem ipsum dolor sit amet, consectetuer adipiscing elit. Aenean
            commodo ligula eget dolor. Aenean massa.
            Cum sociis natoque penatibus et magnis dis parturient montes, nascetur ridiculus mus. Donec quam felis,
            ultricies nec, pellentesque eu, pretium quis, sem. Nulla consequat massa quis enim. Donec pede justo,
            fringilla vel, aliquet nec, vulputate eget, arcu. In enim justo, rhoncus ut, imperdiet a, venenatis vitae,
            justo. Nullam dictum felis eu pede mollis pretium. Integer tincidunt. Cras dapibus. Vivamus elementum semper
            nisi. Aenean vulputate eleifend tellus. Aenean leo ligula, porttitor eu, consequat vitae, eleifend ac, enim.
            Aliquam lorem ante, dapibus in, viverra quis, feugiat a, tellus. Phasellus viverra nulla ut metus varius
            laoreet. Quisque rutrum. Aenean imperdiet. Etiam ultricies nisi vel augue. Curabitur ullamcorper
            ultricies</Typography>
        </FaModalInfo>
        <Divider sx={{marginBottom: '2rem'}}/>
        <ShowCode codeString={exampleCode} scope={{ React, Button, FaModalInfo, Typography }} />

        <Typography variant={"h4"} sx={{marginBottom: '0.5rem', marginTop: '3rem'}}>API Reference</Typography>
        <Typography variant={"body2"} color="text.secondary" sx={{marginBottom: '2rem'}}>
          Complete list of all props supported by the FaModalInfo component.
        </Typography>

        <TableContainer component={Paper} sx={{marginBottom: '2rem', '& code': {fontSize: '0.875rem'}}}>
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor: 'primary.main'}}>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '20%'}}>Prop</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '25%'}}>Type</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '15%'}}>Default</TableCell>
                <TableCell sx={{color: 'white', fontWeight: 'bold', width: '40%'}}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><code>title</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>string | null</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>The main title displayed in the modal header.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>maxWidth</code> *</TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>'xs' | 'sm' | 'md' | 'lg' | 'xl'</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Defines the maximum width of the modal dialog using Material-UI breakpoint values.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>children</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>ReactNode</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>The content to be displayed inside the modal dialog body.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>showClose</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>true</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>When true, displays a close icon button in the top-right corner of the header.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>isOpen</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Controls the open state of the modal when used in controlled mode.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>noHeader</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>boolean</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>When true, hides the entire header section including title and close button.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>setIsOpen</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>(open: boolean) =&gt; void</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Callback function to update the modal's open state, typically used with controlled components.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>outsideClick</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>() =&gt; void</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Callback function triggered when the user clicks outside the modal (on the backdrop).</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>sx</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>SxProps&lt;Theme&gt;</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Material-UI sx prop for custom styling of the dialog component.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>ref</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>Ref</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}><code>-</code></TableCell>
                <TableCell sx={{color: 'text.secondary'}}>Forwarded ref that exposes methods: <code>show()</code> to open the modal, <code>handleClose()</code> to close it, and <code>modalRef</code> for the underlying dialog reference.</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant={"body2"} color="text.secondary" sx={{fontStyle: 'italic'}}>
          * Required prop
        </Typography>
      </Box>
  );
};

export default FactureeCompModalInfoExmpl;
